import { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Input, Select, Button, Badge, Spinner,
  Table, Thead, Tbody, Tr, Th, Td, HStack, InputGroup, InputLeftElement,
  SimpleGrid, Icon, Center,
} from '@chakra-ui/react';
import { BsPlus, BsSearch, BsTicket, BsExclamationCircle, BsArrowRepeat, BsCheckCircle } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LuTicket } from 'react-icons/lu';
import Header from '../../../components/Header';
import PageHeading from '../../../components/shared/PageHeading';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import usePagePermissions from '../../../hooks/usePagePermissions';

const API_URL = `${process.env.REACT_APP_API_URL}/customer-support`;

const customer = JSON.parse(localStorage.getItem("customer"));
  const getHeaders = () => ({
   'ngrok-skip-browser-warning': 'true',
 
   "x-api-key": localStorage.getItem("api-key"),
    "x-access-token": localStorage.getItem("customer-portal"),
});
const statusColorScheme = {
  Open: 'blue',
  'In Progress': 'orange',
  Resolved: 'green',
  Closed: 'gray',
};

const statusDotColor = {
  Open: '#3b82f6',
  'In Progress': '#f97316',
  Resolved: '#22c55e',
  Closed: '#9ca3af',
};

const TABS = ['All', 'Open', 'In Progress', 'Resolved'];

const fmt = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '-';

const StatCard = ({ label, value, icon, iconBg, iconColor, borderColor }) => (
  <Box
    bg="white"
    borderRadius="xl"
    borderWidth="1px"
    borderColor={borderColor || 'gray.200'}
    p={4}
    display="flex"
    alignItems="center"
    gap={3}
  >
    <Flex
      w={10} h={10} borderRadius="lg" bg={iconBg}
      alignItems="center" justifyContent="center" flexShrink={0}
    >
      <Icon as={icon} color={iconColor} boxSize={5} />
    </Flex>
    <Box>
      <Text fontSize="xl" fontWeight="bold" color="gray.900" lineHeight={1}>{value}</Text>
      <Text fontSize="xs" color="gray.500" mt={0.5}>{label}</Text>
    </Box>
  </Box>
);

const MyTickets = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: [] });
  const [loading, setLoading] = useState(true);

  const { loading: featurePermissionsLoading, can } = usePagePermissions('support/tickets');

  const ticketListPermission = can("ticket-list");
  const ticketCreatePermission = can("ticket-create");
  const ticketViewPermission = can("ticket-view");

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status_id', statusFilter);
      if (productFilter) params.set('product_id', productFilter);
      const { data: res } = await axios.get(`${API_URL}/ticket-list?${params.toString()}`, { headers: getHeaders() });
      setTickets(res?.rows || res?.data || []);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, productFilter]);

  const loadStats = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API_URL}/ticket-stats`, { headers: getHeaders() });
      setStats(res || { total: 0, by_status: [] });
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (featurePermissionsLoading || !ticketListPermission) return;
    loadStats();
  }, [loadStats, featurePermissionsLoading, ticketListPermission]);
  useEffect(() => {
    if (featurePermissionsLoading || !ticketListPermission) return;
    loadTickets();
  }, [loadTickets, featurePermissionsLoading, ticketListPermission]);

  const byStatus = stats.by_status || [];
  const getCount = (name) => byStatus.find((s) => s.name === name)?.count ?? 0;
  const getStatusId = (name) => byStatus.find((s) => s.name === name)?.status_id ?? '';

  const products = Object.values(
    tickets.reduce((acc, t) => {
      if (t.product?.id) acc[t.product.id] = { id: t.product.id, name: t.product.name };
      return acc;
    }, {})
  );

  const tabCount = (tab) => {
    if (tab === 'All') return stats.total ?? tickets.length;
    return getCount(tab);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setStatusFilter(tab === 'All' ? '' : getStatusId(tab));
  };

  if (featurePermissionsLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        </BoxWrapper>
      </>
    );
  }

  if (!ticketListPermission) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                No Access
              </Text>
              <Text fontSize="sm" color="gray.500">
                You don't have permission to view this sections.
              </Text>
            </Box>
          </Center>
        </BoxWrapper>
      </>
    );
  }

  return (
    <>
      <Header />
      <BoxWrapper>
        <Box>
          {/* Header */}
          <Box py={4} borderBottomWidth="1px" mb={5}>
            <PageHeading icon={LuTicket} title="My Tickets" subtitle="View and manage all your support tickets" mb={0}>
            {ticketCreatePermission && (
              <Button
                size="sm"
                bg="var(--brand-primary)"
                color="white"
                _hover={{ bg: 'var(--brand-primary-active)' }}
                onClick={() => navigate('/support/create-ticket')}
                leftIcon={<BsPlus />}
                borderRadius="lg"
              >
                Create Ticket
              </Button>
            )}
            </PageHeading>
          </Box>

          {/* Stat Cards */}
          <SimpleGrid columns={4} gap={3} mb={5}>
            <StatCard
              label="Total Tickets"
              value={stats.total ?? 0}
              icon={BsTicket}
              iconBg="blue.50"
              iconColor="blue.500"
              borderColor="blue.100"
            />
            <StatCard
              label="Open"
              value={getCount('Open')}
              icon={BsExclamationCircle}
              iconBg="orange.50"
              iconColor="orange.500"
              borderColor="orange.100"
            />
            <StatCard
              label="In Progress"
              value={getCount('In Progress')}
              icon={BsArrowRepeat}
              iconBg="purple.50"
              iconColor="purple.500"
              borderColor="purple.100"
            />
            <StatCard
              label="Resolved"
              value={getCount('Resolved')}
              icon={BsCheckCircle}
              iconBg="green.50"
              iconColor="green.500"
              borderColor="green.100"
            />
          </SimpleGrid>

          {/* Search + Filters */}
          <Flex gap={3} mb={4} alignItems="center">
            <InputGroup flex={1} size="sm">
              <InputLeftElement pointerEvents="none">
                <BsSearch color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search tickets by ID or title..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                borderRadius="lg"
                bg="white"
              />
            </InputGroup>
            <Select
              size="sm"
              w="180px"
              placeholder="All Products"
              value={productFilter}
              onChange={(e) => setProductFilter(e.target.value)}
              borderRadius="lg"
              bg="white"
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
            <Select
              size="sm"
              w="180px"
              placeholder="All Statuses"
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setActiveTab('All'); }}
              borderRadius="lg"
              bg="white"
            >
              {byStatus.map((s) => (
                <option key={s.status_id} value={s.status_id}>{s.name}</option>
              ))}
            </Select>
          </Flex>

          {/* Status Tabs */}
          <HStack gap={1} mb={4} bg="gray.100" p={1} borderRadius="xl" w="fit-content">
            {TABS.map((tab) => {
              const isActive = activeTab === tab;
              return (
                <Button
                  key={tab}
                  size="sm"
                  variant="unstyled"
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  px={3}
                  h={8}
                  borderRadius="lg"
                  bg={isActive ? 'white' : 'transparent'}
                  color={isActive ? 'gray.900' : 'gray.500'}
                  fontWeight={isActive ? 'semibold' : 'medium'}
                  boxShadow={isActive ? 'sm' : 'none'}
                  _hover={isActive ? {} : { color: 'gray.700' }}
                  onClick={() => handleTabChange(tab)}
                  fontSize="xs"
                  transition="all 0.15s"
                >
                  {tab !== 'All' && (
                    <Box
                      w={1.5} h={1.5} borderRadius="full"
                      bg={isActive ? (statusDotColor[tab] || 'gray.400') : 'gray.400'}
                      flexShrink={0}
                    />
                  )}
                  {tab}
                  <Box
                    as="span"
                    bg={isActive ? 'gray.100' : 'gray.200'}
                    color={isActive ? 'gray.700' : 'gray.500'}
                    fontSize="2xs"
                    fontWeight="semibold"
                    px={1.5}
                    py={0.5}
                    borderRadius="full"
                    lineHeight={1.4}
                  >
                    {tabCount(tab)}
                  </Box>
                </Button>
              );
            })}
          </HStack>

          {/* Table */}
          <Box bg="white" borderRadius="xl" borderWidth="1px" overflow="hidden">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>Ticket ID</Th>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>Title</Th>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>Product</Th>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>Category</Th>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>Status</Th>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>Created</Th>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>First Response</Th>
                  <Th py={3} color="gray.500" fontWeight="semibold" fontSize="xs" textTransform="none" letterSpacing={0}>Closed</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={16}>
                      <Flex direction="column" alignItems="center" gap={2}>
                        <Spinner size="sm" color="var(--brand-primary)" />
                        <Text fontSize="xs" color="gray.400">Loading tickets...</Text>
                      </Flex>
                    </Td>
                  </Tr>
                ) : tickets.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={16}>
                      <Flex direction="column" alignItems="center" gap={2}>
                        <Icon as={BsTicket} color="gray.300" boxSize={8} />
                        <Text fontSize="sm" color="gray.500" fontWeight="medium">No tickets found</Text>
                        <Text fontSize="xs" color="gray.400">Try adjusting your search or filters</Text>
                      </Flex>
                    </Td>
                  </Tr>
                ) : (
                  tickets.map((ticket, i) => (
                    <Tr
                      key={ticket.id}
                      cursor={ticketViewPermission ? 'pointer' : 'default'}
                      _hover={ticketViewPermission ? { bg: 'blue.50' } : undefined}
                      onClick={ticketViewPermission ? () => navigate(`/support/ticket/${ticket.id}`) : undefined}
                      borderTopWidth={i === 0 ? 0 : '1px'}
                      transition="background 0.1s"
                    >
                      <Td py={3.5}>
                        <Text fontSize="xs" color="blue.600" fontWeight="semibold">{ticket.ticket_number}</Text>
                      </Td>
                      <Td py={3.5} maxW="220px">
                        <Text fontSize="xs" fontWeight="medium" color="gray.900" noOfLines={1}>{ticket.subject}</Text>
                      </Td>
                      <Td py={3.5}>
                        <Text fontSize="xs" color="gray.500">{ticket.product?.name || '-'}</Text>
                      </Td>
                      <Td py={3.5}>
                        <Text fontSize="xs" color="gray.500">{ticket.category?.name || '-'}</Text>
                      </Td>
                      <Td py={3.5}>
                        <HStack gap={1.5}>
                          <Box
                            w={1.5} h={1.5} borderRadius="full" flexShrink={0}
                            bg={statusDotColor[ticket.status?.name] || 'gray.400'}
                          />
                          <Badge
                            colorScheme={statusColorScheme[ticket.status?.name] || 'gray'}
                            borderRadius="full"
                            fontSize="2xs"
                            px={2}
                            py={0.5}
                            textTransform="none"
                            fontWeight="semibold"
                          >
                            {ticket.status?.name}
                          </Badge>
                        </HStack>
                      </Td>
                      <Td py={3.5}>
                        <Text fontSize="xs" color="gray.500">{fmt(ticket.createdAt)}</Text>
                      </Td>
                      <Td py={3.5}>
                        <Text fontSize="xs" color={ticket.first_response_at ? 'green.600' : 'gray.400'}>
                          {fmt(ticket.first_response_at)}
                        </Text>
                      </Td>
                      <Td py={3.5}>
                        <Text fontSize="xs" color="gray.500">{fmt(ticket.closed_at)}</Text>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </BoxWrapper>
    </>
  );
};

export default MyTickets;
